import { Directive, ElementRef, inject, NgZone, OnDestroy, OnInit, output } from '@angular/core';

/**
 * Reports whether a line-clamped element actually cuts its text, so a
 * Show more appears only when there is more to show. Measured, not guessed
 * from a character count: how many characters fit in N lines depends on
 * the panel width, which the user can drag.
 *
 *   <p class="clamped" (appClampOverflow)="overflowing = $event">…</p>
 *
 * Re-checks when the element resizes (panel drag, window resize, first
 * render of a content-visibility:auto row) or its text changes (English ↔
 * original language). Emits only on change.
 */
@Directive({
  selector: '[appClampOverflow]',
  standalone: false,
})
export class ClampOverflowDirective implements OnInit, OnDestroy {
  readonly appClampOverflow = output<boolean>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private resize: ResizeObserver | null = null;
  private mutation: MutationObserver | null = null;
  private last: boolean | null = null;

  ngOnInit(): void {
    const el = this.host.nativeElement;
    // Observers run outside Angular; only an actual change re-enters it.
    this.zone.runOutsideAngular(() => {
      this.resize = new ResizeObserver(() => this.check());
      this.resize.observe(el);
      this.mutation = new MutationObserver(() => this.check());
      this.mutation.observe(el, { childList: true, characterData: true, subtree: true });
    });
    this.check();
  }

  ngOnDestroy(): void {
    this.resize?.disconnect();
    this.mutation?.disconnect();
  }

  private check(): void {
    const el = this.host.nativeElement;
    // Unrendered (display:none, or skipped by content-visibility): wait for the resize.
    if (!el.clientHeight) return;
    // 1px of slack: subpixel line heights can round scrollHeight up.
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    if (overflowing === this.last) return;
    this.last = overflowing;
    this.zone.run(() => this.appClampOverflow.emit(overflowing));
  }
}
